import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useMediaQuery, useTheme, InputAdornment } from '@mui/material'
import { TextField as ShellTextField, TextFieldProps } from '@e3dc-react/shell/Elements/InputFields/TextField'
import { makeStyles } from 'tss-react/mui'
import { useSuffixFieldStyles } from '../../hooks/useSuffixFieldStyles'

type Props = {
  name: string
  hasInputAdornment?: boolean
} & Omit<TextFieldProps, 'name'>

export const useInputStyles = makeStyles()(theme => ({
  suffix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 34,
    marginLeft: theme.spacing(2),
    paddingInline: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.shades.shade02}`,
    '& > span': {
      minWidth: 48,
      textAlign: 'center',
    },
  },
  emptyAdornment: {
    paddingRight: theme.spacing(2),
  },
}))

/**
 * Shell TextField with react hook form
 * @Info: to support properties like mask we use shell text field and not mui directly.
 */
const TextField: React.FC<Props> = ({ name, suffix, endAdornment, hasInputAdornment = false, fullWidth, type, ...props }) => {
  const { control } = useFormContext()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const applyFullWidth = fullWidth || downSm
  const { classes } = useInputStyles()
  const { classes: suffixFieldClasses, cx } = useSuffixFieldStyles()

  const resolvedEndAdornment = suffix ? (
    <div className={classes.suffix}>
      <span>{suffix}</span>
    </div>
  ) : (
    endAdornment ?? (hasInputAdornment ? <InputAdornment position="end" className={classes.emptyAdornment} /> : undefined)
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <ShellTextField
          {...field}
          {...props}
          error={!!error}
          helperText={error?.message}
          type={type}
          fullWidth={applyFullWidth}
          useTooltipErrorMessage={false}
          endAdornment={resolvedEndAdornment}
          className={suffix ? cx(props.className, suffixFieldClasses.suffixField) : props.className}
        />
      )}
    />
  )
}

TextField.displayName = 'TextField'

export default TextField
